import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import "./ChatbotWidget.css";

const SESSION_STORAGE_KEY = "chatbot_session_id";
const GREETING = "Hi, I'm ThriftBot. Ask me about your products, listings, or performance.";

function makeSessionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `session-${Date.now()}`;
}

// Cache image previews in sessionStorage so they survive page navigation
function saveImgCache(sid, previewUrl) {
  try {
    const list = JSON.parse(sessionStorage.getItem(`chat_imgs_${sid}`) || "[]");
    list.push(previewUrl);
    sessionStorage.setItem(`chat_imgs_${sid}`, JSON.stringify(list));
  } catch (_) {}
}
function getImgCache(sid) {
  try { return JSON.parse(sessionStorage.getItem(`chat_imgs_${sid}`) || "[]"); }
  catch (_) { return []; }
}

const ChatbotWidget = ({ isOpen, onToggle, onClose }) => {
  const { companyId } = useAuth();
  const [mode, setMode] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [toolsUsed, setToolsUsed] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [messages, setMessages] = useState([{ role: "model", text: GREETING }]);

  // History
  const [view, setView] = useState("chat"); // 'chat' | 'history'
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [viewedSession, setViewedSession] = useState(null);

  const listRef = useRef(null);
  const restoredRef = useRef(false); // true if session ID came from localStorage

  // Init or restore session
  useEffect(() => {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      setSessionId(existing);
      restoredRef.current = true;
      return;
    }
    const id = makeSessionId();
    setSessionId(id);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
  }, []);

  // Auto-load messages for a restored session (e.g. after page refresh)
  useEffect(() => {
    if (!restoredRef.current || !sessionId || !companyId) return;
    restoredRef.current = false; // only run once
    api.get(`/api/chat/${companyId}/sessions/${sessionId}`)
      .then((res) => {
        if (res?.data?.success && res.data.messages.length > 0) {
          const imgCache = getImgCache(sessionId);
          let imgIdx = 0;
          const loaded = res.data.messages.map((m) => {
            const msg = { role: m.role, text: m.message || "", hasImage: Boolean(m.has_image) };
            if (m.has_image && imgCache[imgIdx]) msg.imagePreview = imgCache[imgIdx++];
            return msg;
          });
          setMessages(loaded);
        }
      })
      .catch(() => {});
  }, [sessionId, companyId]);

  // Auto-scroll
  useEffect(() => {
    if (!listRef.current || !isOpen) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, isOpen, viewedSession]);

  const canSend = useMemo(
    () => Boolean(companyId) && Boolean(sessionId) && (message.trim().length > 0 || Boolean(selectedImage)) && !sending,
    [companyId, sessionId, message, selectedImage, sending]
  );

  const handleNewChat = useCallback(() => {
    const id = makeSessionId();
    setSessionId(id);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    setMessages([{ role: "model", text: GREETING }]);
    setSelectedImage(null);
    setMessage("");
    setView("chat");
    setViewedSession(null);
  }, []);

  const fetchSessions = useCallback(async () => {
    if (!companyId) return;
    setLoadingSessions(true);
    try {
      const res = await api.get(`/api/chat/${companyId}/sessions`);
      if (res?.data?.success) setSessions(res.data.sessions || []);
    } catch (_) {}
    setLoadingSessions(false);
  }, [companyId]);

  const openHistory = useCallback(() => {
    setView("history");
    fetchSessions();
  }, [fetchSessions]);

  const loadSession = useCallback(async (session) => {
    try {
      const res = await api.get(`/api/chat/${companyId}/sessions/${session.id}`);
      if (res?.data?.success) {
        const imgCache = getImgCache(session.id);
        let imgIdx = 0;
        const loaded = res.data.messages.map((m) => {
          const msg = {
            role: m.role,
            text: m.message || "",
            hasImage: Boolean(m.has_image),
          };
          if (m.has_image && imgCache[imgIdx]) {
            msg.imagePreview = imgCache[imgIdx++];
          }
          return msg;
        });
        setMessages(loaded.length > 0 ? loaded : [{ role: "model", text: GREETING }]);
        setSessionId(session.id);
        localStorage.setItem(SESSION_STORAGE_KEY, session.id);
        setViewedSession({ title: session.title });
        setView("chat");
        setSelectedImage(null);
        setMessage("");
      }
    } catch (_) {}
  }, [companyId]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage({
        base64: e.target.result,
        mimeType: file.type || "image/jpeg",
        previewUrl: e.target.result,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleRemoveImage = () => setSelectedImage(null);

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !selectedImage) return;
    if (!companyId || !sessionId || sending) return;

    const imageToSend = selectedImage;
    const userMessage = { role: "user", text: trimmedMessage, imagePreview: imageToSend?.previewUrl || null };
    if (imageToSend?.previewUrl) saveImgCache(sessionId, imageToSend.previewUrl);
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setSelectedImage(null);
    setSending(true);

    try {
      const response = await api.post(`/api/chat/${companyId}`, {
        message: trimmedMessage,
        session_id: sessionId,
        mode,
        image: imageToSend ? imageToSend.base64 : undefined,
        mimeType: imageToSend ? imageToSend.mimeType : undefined,
      });

      if (response?.data?.success) {
        setMessages((prev) => [
          ...prev,
          { role: "model", text: response.data.reply || "No reply received." },
        ]);
        setToolsUsed(Array.isArray(response.data.toolsUsed) ? response.data.toolsUsed : []);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "model", text: "I couldn't process that request right now. Please try again." },
        ]);
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message;
      setMessages((prev) => [
        ...prev,
        { role: "model", text: serverMsg || "Chat service is unavailable right now. Please try again shortly." },
      ]);
      setToolsUsed([]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const activeMessages = messages;

  return (
    <>
      <button
        type="button"
        className="chatbot-fab"
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
        onClick={onToggle}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <circle cx="9" cy="10" r="1" fill="currentColor" />
          <circle cx="15" cy="10" r="1" fill="currentColor" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="chatbot-overlay" onClick={onClose} />
          <section className="chatbot-widget" aria-label="ThriftBot Chatbox">

            {/* â”€â”€ HEADER â”€â”€ */}
            <header className="chatbot-widget-header">
              {view === "history" ? (
                <>
                  <div><h3>Past Chats</h3></div>
                  <div className="chatbot-widget-actions">
                    <button type="button" className="chatbot-back-btn" onClick={() => setView("chat")}>← Back</button>
                    <button type="button" onClick={onClose} aria-label="Close chatbot">×</button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3>ThriftBot</h3>
                    <p>{viewedSession ? viewedSession.title : "AI Assistant"}</p>
                  </div>
                  <div className="chatbot-widget-actions">
                    <select value={mode} onChange={(e) => setMode(e.target.value)} disabled={sending}>
                      <option value="general">General</option>
                      <option value="company">Company Specific</option>
                    </select>
                    <button type="button" onClick={openHistory} title="View chat history" className="chatbot-history-btn">🕐</button>
                    <button type="button" onClick={onClose} aria-label="Close chatbot">×</button>
                  </div>
                </>
              )}
            </header>

            {/* â”€â”€ HISTORY LIST â”€â”€ */}
            {view === "history" && (
              <div className="chatbot-history-list">
                <button type="button" className="chatbot-new-chat-full" onClick={handleNewChat}>
                  + New Chat
                </button>
                {loadingSessions ? (
                  <p className="chatbot-history-empty">Loading...</p>
                ) : sessions.length === 0 ? (
                  <p className="chatbot-history-empty">No past chats yet.</p>
                ) : (
                  sessions.map((s) => (
                    <button type="button" key={s.id} className="chatbot-session-item" onClick={() => loadSession(s)}>
                      <span className="chatbot-session-item-title">{s.title}</span>
                      <span className="chatbot-session-item-date">{formatDate(s.updated_at)}</span>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* â”€â”€ MESSAGES (chat or viewing) â”€â”€ */}
            {view !== "history" && (
              <>
                {viewedSession && view === "chat" && (
                  <div className="chatbot-viewing-banner">
                    Resumed: <strong>{viewedSession.title}</strong> &mdash;{" "}
                    <button type="button" onClick={handleNewChat}>New Chat</button>
                  </div>
                )}
                <div className="chatbot-widget-body" ref={listRef}>
                  {activeMessages.map((item, index) => (
                    <div key={`${item.role}-${index}`} className={`chatbot-bubble ${item.role}`}>
                      <span className="chatbot-role">{item.role === "user" ? "You" : "ThriftBot"}</span>
                      {item.imagePreview && (
                        <img src={item.imagePreview} alt="uploaded" className="chatbot-bubble-image" />
                      )}
                      {item.hasImage && !item.imagePreview && (
                        <div className="chatbot-bubble-img-placeholder">ðŸ“· Image</div>
                      )}
                      {item.text && <p>{item.text}</p>}
                    </div>
                  ))}
                  {sending && (
                    <div className="chatbot-bubble model">
                      <span className="chatbot-role">ThriftBot</span>
                      <p>Thinking...</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* â”€â”€ TOOLS â”€â”€ */}
            {view === "chat" && toolsUsed.length > 0 && (
              <div className="chatbot-tools">Tools: {toolsUsed.join(", ")}</div>
            )}

            {/* â”€â”€ FOOTER (only in chat view) â”€â”€ */}
            {view === "chat" && (
              <footer className="chatbot-widget-footer">
                {selectedImage && (
                  <div className="chatbot-widget-image-preview-wrap">
                    <img src={selectedImage.previewUrl} alt={selectedImage.name} className="chatbot-widget-image-preview" />
                    <button type="button" className="chatbot-widget-image-remove" onClick={handleRemoveImage} title="Remove image">&times;</button>
                  </div>
                )}
                <div className="chatbot-widget-input-row">
                  <textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about products, sales, or marketplaces..."
                    rows={2}
                    disabled={sending}
                  />
                  <div className="chatbot-widget-btns">
                    <button
                      type="button"
                      className="chatbot-widget-upload-btn"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      disabled={sending}
                      title="Attach image"
                    >
                      ðŸ“Ž
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleImageSelect}
                    />
                    <button type="button" onClick={handleSend} disabled={!canSend}>
                      Send
                    </button>
                  </div>
                </div>
              </footer>
            )}

          </section>
        </>
      )}
    </>
  );
};

export default ChatbotWidget;
